import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate, useParams } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { BuyInvoicePaymentApi, BuyInvoicePaymentDataType } from "@/api/BuyInvoicePaymentApi";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import { Col, Container, Row } from "reactstrap";
import { formatDate } from "@utils/Api";
import LoadingScreen from "@components/content/LoadingScreen";
import { getPaymentMethodLabel } from "@utils/Utils";
import { FetchResponse } from "@/api/ApiClient";
import { BuyInvoiceApi, BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { BuyInvoiceFormDataKeys } from "@/generated/BuyInvoiceFormDataKeys";
import { buyInvoiceRelatedAttributes } from "@/formTypes/BuyInvoiceDataType";
import { ProviderApi, ProviderDataType } from "@/api/ProviderApi";

const BuyInvoicePaymentIndex = () => {
  const navigate = useNavigate();
  const params = useParams();
  const modelRef = useRef<ModelIndexHandle>(null);
  const { id: buyInvoiceId } = params;
  const [buyInvoice, setBuyInvoice] = useState<BuyInvoiceDataType>({})
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paymentAmount, setPaymentAmount] = useState<Number>(Number(0));
  const [pendingAmount, setPendingAmount] = useState<Number>(Number(0));
  const [notesAmount, setNotesAmount] = useState<Number>(Number(0));
  const [paymentList, setPaymentList] = useState<Array<BuyInvoicePaymentDataType>>([]);
  const [provider, setProvider] = useState<ProviderDataType>({});

  useEffect(() => {
    if (buyInvoiceId) {
      const run = async (buyInvoiceId: string) => {
        try {
          const invoice = await BuyInvoiceApi.get(buyInvoiceId, {
            selectionSet: [
              ...BuyInvoiceFormDataKeys.filter(k => !buyInvoiceRelatedAttributes.includes(k)),
              "currency.*",
            ]
          });
          setBuyInvoice(invoice);
          const provider = await ProviderApi.get(`${invoice.providerId}`)
          setProvider(provider);
        } finally {
          setIsLoading(false)
        }
      }
      run(buyInvoiceId);
      setIsLoading(true)
    }
  }, [buyInvoiceId]);

  useEffect(() => {
    calculatePaymentTotal(paymentList)
  }, [paymentList])

  const deleteRecord = async (id: string) => {
    const { errors } = await BuyInvoicePaymentApi.delete(id);
    if (errors?.length) {
      showAlert('danger', 'El abono de la factura de la factura electronica de Compra no puedo ser eliminado');
    } else {
      showAlert('success', 'El abono de la factura electronica de Compra fue eliminado correctamente');
    }
    modelRef.current?.refetch();
  }

  const calculatePaymentTotal = (paymentList: Array<BuyInvoicePaymentDataType>) => {
    let invoiceTotal = Number(0);
    let notesAmount = Number(0);
    let paymentAmount = Number(0);
    if (paymentList && paymentList.length > 0) {
      paymentAmount = paymentList.reduce((a, p) => {
        const amount = Number(p.monto)
        return a + amount;
      }, Number(0))
    }
    setPaymentAmount(paymentAmount)

    // if (buyInvoice.providerNotes && buyInvoice.providerNotes?.length > 0) {
    //   notesAmount = buyInvoice.providerNotes?.reduce((a: number, p: ProviderNoteDataType) => {
    //     let amount = Number(p.total)
    //     if (p.tipo == `${PROVIDER_CREDITNOTE_TYPE_CODE}`) {
    //       amount = -1 * amount
    //     }
    //     return a + amount;
    //   }, Number(0))
    // }
    // setNotesAmount(notesAmount)
    setNotesAmount(0)

    if (buyInvoice.total) {
      invoiceTotal = buyInvoice.total
    }

    setPendingAmount(invoiceTotal - paymentAmount - notesAmount)
  }

  const getInvoicePayments = async (): Promise<FetchResponse<BuyInvoicePaymentDataType>> => {
    return await BuyInvoicePaymentApi.fetchAllByBuyInvoice(`${buyInvoiceId}`)
  }

  return (
    <PageContainer
      title="Lista Abonos de Factura de Electronica de Compra"
      backUrl="/user/buyInvoices"
      primaryActions={[
        {
          onClick: () => {
            navigate(`/user/buyInvoices/payments/add/${buyInvoiceId}`)
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
    >
      <Container fluid>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Provedor:</b></h5>
          </Col>
          <Col>
            <h5><b>{provider.nombre}</b></h5>
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Fecha:</b>
          </Col>
          <Col>
            {formatDate(buyInvoice?.fecha)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Numero Factura:</b>
          </Col>
          <Col>
            {buyInvoice?.numero}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Subtotal:</b>
          </Col>
          <Col>
            {formatMoneyValue(buyInvoice?.subtotal, buyInvoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Descuento:</b>
          </Col>
          <Col>
            {formatMoneyValue(buyInvoice?.descuento, buyInvoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Impuestos:</b>
          </Col>
          <Col>
            {formatMoneyValue(buyInvoice?.impuestos, buyInvoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Total:</b></h5>
          </Col>
          <Col>
            <h5><b>{formatMoneyValue(buyInvoice?.total, buyInvoice?.currency?.currency_id)}</b></h5>
          </Col>
        </Row>
        <hr />
      </Container>
      <ModelIndex<BuyInvoicePaymentDataType>
        messages={{
          noResults: "No hay abonos para este factura"
        }}
        ref={modelRef}
        onDataFetched={(data) => setPaymentList(data)}
        fetchData={() => getInvoicePayments()}
        primaryProperties={[
          {
            key: () => "Forma de Pago",
            highlighted: true
          },
          {
            key: (d) => { return getPaymentMethodLabel(d.formaPago) },
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "2", xs: "4" },
            { sm: "8", xs: "8" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              return formatDate(data.createdAt)
            },
            title: "Fecha",
          },
          {
            key: "recibo",
            title: "Recibo",
          },
          {
            key: (data) => {
              return formatMoneyValue(data.monto, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" }
          ]
        }
        listActions={[
          {
            href: `/user/buyInvoices/payments/edit/{:id}/${buyInvoiceId}`,
            actionTitle: "Editar Abono",
            iconProps: { icon: faEdit }
          },
          {
            actionTitle: "Eliminar Abono",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Abono",
              confirmMessage: "¿Seguro que desea eliminar esta Abono?",
              confirmBtnText: "Eliminar"
            },
            onClick: (id: string) => { deleteRecord(id) }
          }
        ]}
      >
      </ModelIndex>
      <hr />
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(buyInvoice?.total, buyInvoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total Notas:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(notesAmount, buyInvoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total Abonos:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(paymentAmount, buyInvoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Saldo:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(pendingAmount, buyInvoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default BuyInvoicePaymentIndex;
